import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Navbar } from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Progress } from '../components/ui/progress';
import { 
  Play, CheckCircle, ArrowRight, BookOpen, Target, 
  Users, ListChecks, HelpCircle, Video,
  ChevronRight, Award, ClipboardCheck, FileText
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ParcoursPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, logEvent } = useAuth();
  const [parcours, setParcours] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('presentation');
  const [quizResult, setQuizResult] = useState(null);
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [currentVideo, setCurrentVideo] = useState(null);

  useEffect(() => {
    fetchParcours();
    if (user) {
      fetchQuizResult();
    }
  }, [id, user]);

  const fetchParcours = async () => {
    try {
      const response = await axios.get(`${API}/parcours/${id}`);
      setParcours(response.data);
      logEvent('parcours_opened', id);
    } catch (error) {
      console.error('Error fetching parcours:', error);
      toast.error('Erreur lors du chargement du parcours');
    } finally {
      setLoading(false);
    }
  };

  const fetchQuizResult = async () => {
    try {
      const response = await axios.get(`${API}/quiz/result/${user.id}/${id}`);
      setQuizResult(response.data);
    } catch (error) {
      console.error('Error fetching quiz result:', error);
    }
  };

  const handleVideoPlay = (etapeIndex) => {
    const etape = parcours.etapes[etapeIndex];
    logEvent('video_start', id, `scenario_${etapeIndex + 1}`);
    
    if (etape.video_url) {
      setCurrentVideo({
        title: etape.titre,
        url: etape.video_url
      });
      setVideoModalOpen(true);
      toast.success('Ouverture de la vidéo de démonstration');
    } else {
      toast.info('Vidéo en cours de lecture (placeholder)');
    }
  };

  const handleSectionChange = (section) => {
    setActiveSection(section);
    logEvent('section_consulted', id, section);
  };

  const getVideoThumbnail = (videoUrl) => {
    // Générer une miniature basée sur l'ID de la vidéo Directus
    if (videoUrl && videoUrl.includes('directus.app/assets/')) {
      const videoId = videoUrl.split('/').pop();
      
      // Miniatures spécifiques selon le type de vidéo
      const thumbnails = {
        '181ccee8-9637-43bc-9656-b319fe67b7f2': 'https://images.unsplash.com/photo-1554224154-26032ffc0d07?w=400&h=225&fit=crop&crop=center', // Synchronisation AO Optima
        '4abb0a51-d8a2-4cac-bfee-ad4c59507b27': 'https://images.unsplash.com/photo-1611224923853-80b023f02d71?w=400&h=225&fit=crop&crop=center', // Suivi deadline
        '41e46602-483b-4163-a6a8-d57257fc229f': 'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=400&h=225&fit=crop&crop=center', // Informations de facturation
        '6025c5b2-98ad-4e3c-9b12-cb57f9ac2dfd': 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=400&h=225&fit=crop&crop=center', // Chantier
        '13d4d051-32fb-4f8e-bb8d-c68ee10bec05': 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=400&h=225&fit=crop&crop=center', // Mise en demeure
        '113f9d61-a751-4d07-9f46-8f20e8d0a511': 'https://images.unsplash.com/photo-1586953208448-b95a79798f07?w=400&h=225&fit=crop&crop=center', // Traiter devis fournisseur
        '8abc87ea-5796-4cf5-960a-cf0d32139677': 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=400&h=225&fit=crop&crop=center'  // Traiter facture fournisseur
      };
      
      return thumbnails[videoId] || 'https://images.unsplash.com/photo-1611224923853-80b023f02d71?w=400&h=225&fit=crop&crop=center';
    }
    return null;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f2f2f2]">
        <Navbar />
        <div className="container mx-auto px-4 py-8">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-200 rounded w-1/3"></div>
            <div className="h-4 bg-gray-200 rounded w-2/3"></div>
            <div className="h-64 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!parcours) {
    return (
      <div className="min-h-screen bg-[#f2f2f2]">
        <Navbar />
        <div className="container mx-auto px-4 py-8 text-center">
          <h1 className="text-2xl font-bold text-[#571925]">Parcours non trouvé</h1>
          <Link to="/vipplus">
            <Button className="mt-4">Retour à l'accueil</Button>
          </Link>
        </div>
      </div>
    );
  }

  // Sections de navigation restructurées
  const sections = [
    { id: 'presentation', label: 'Présentation du parcours', icon: BookOpen },
    ...(parcours.etapes || []).map((etape, index) => ({
      id: `scenario${index + 1}`,
      label: etape.titre?.replace('Scénario - ', '') || `Scénario ${index + 1}`,
      icon: Play
    })),
    { id: 'quiz', label: 'Quiz', icon: HelpCircle },
    { id: 'satisfaction', label: 'Évaluation de la formation', icon: ClipboardCheck },
  ];

  return (
    <div className="min-h-screen bg-[#f2f2f2]" data-testid="parcours-page">
      <Navbar />
      
      {/* Header */}
      <div className="bg-[#571925] text-white py-8">
        <div className="container mx-auto px-4 md:px-8">
          <div className="flex items-center gap-2 text-sm text-white/70 mb-4">
            <Link to="/vipplus" className="hover:text-white">Accueil</Link>
            <ChevronRight className="h-4 w-4" />
            <span>Parcours</span>
            <ChevronRight className="h-4 w-4" />
            <span className="text-white">{parcours.title}</span>
          </div>
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold mb-2 font-['Outfit']">{parcours.title}</h1>
              <p className="text-white/80 max-w-2xl">{parcours.description}</p>
            </div>
            {quizResult?.passed && (
              <div className="flex items-center gap-2 bg-green-500 px-4 py-2 rounded-full">
                <Award className="h-5 w-5" />
                <span className="font-medium">Validé</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 md:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar */}
          <aside className="lg:w-72 shrink-0">
            <Card className="sticky top-24">
              <CardContent className="p-4">
                <nav className="space-y-1">
                  {sections.map((section) => {
                    const Icon = section.icon;
                    return (
                      <button
                        key={section.id}
                        onClick={() => handleSectionChange(section.id)}
                        className={`sidebar-link w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                          activeSection === section.id 
                            ? 'bg-[#571925] text-white' 
                            : 'text-gray-700 hover:bg-gray-100'
                        }`}
                        data-testid={`nav-${section.id}`}
                      >
                        <Icon className="h-4 w-4 shrink-0" />
                        <span className="truncate">{section.label}</span>
                      </button>
                    );
                  })}
                </nav>
                
                {/* Progress */}
                <div className="mt-6 pt-6 border-t">
                  <p className="text-sm text-gray-600 mb-2">Progression</p>
                  <Progress 
                    value={quizResult?.passed ? 100 : (quizResult ? 80 : 0)} 
                    className="h-2"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {quizResult?.passed ? 'Parcours terminé' : quizResult ? 'Quiz à valider' : 'Non commencé'}
                  </p>
                </div>
              </CardContent>
            </Card>
          </aside>

          {/* Main Content */}
          <main className="flex-1 min-w-0">
            {/* Présentation Section (Description + Objectifs + Bonnes Pratiques) */}
            {activeSection === 'presentation' && (
              <div className="space-y-6 animate-fade-in" data-testid="section-presentation">
                {/* Cartouche Description */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-xl text-[#cc5759] font-['Outfit'] flex items-center gap-2">
                      <FileText className="h-5 w-5" />
                      Description du processus
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Processus */}
                    <div>
                      <h3 className="font-semibold text-[#1a1a1a] mb-2">Processus</h3>
                      <p className="text-gray-700">{parcours.processus}</p>
                    </div>

                    {/* Fonctionnalités */}
                    <div>
                      <h3 className="font-semibold text-[#1a1a1a] mb-3">Nouvelles fonctionnalités Salesforce</h3>
                      <div className="space-y-3">
                        {parcours.fonctionnalites?.map((f, i) => (
                          <div key={i} className="bg-gray-50 p-4 rounded-lg">
                            <h4 className="font-medium text-[#571925]">{f.titre}</h4>
                            <p className="text-sm text-gray-600 mt-1">→ {f.description}</p>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Modules Salesforce couverts */}
                    {parcours.modules_salesforce && parcours.modules_salesforce.length > 0 && (
                      <div>
                        <h3 className="font-semibold text-[#1a1a1a] mb-3">Modules Salesforce couverts</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {parcours.modules_salesforce.map((module, i) => (
                            <div key={i} className="bg-[#571925]/5 border border-[#571925]/20 p-3 rounded-lg">
                              <h4 className="font-medium text-[#571925] text-sm">{module.nom}</h4>
                              <p className="text-xs text-gray-600 mt-1">{module.description}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Flux en entrée */}
                    <div>
                      <h3 className="font-semibold text-[#1a1a1a] mb-2">Flux et éléments en entrée</h3>
                      <ul className="list-disc list-inside space-y-1 text-gray-700">
                        {parcours.inputs?.map((input, i) => (
                          <li key={i}>{input}</li>
                        ))}
                      </ul>
                    </div>

                    {/* Acteurs */}
                    <div>
                      <h3 className="font-semibold text-[#1a1a1a] mb-2 flex items-center gap-2">
                        <Users className="h-4 w-4 text-[#cc5759]" />
                        Acteurs impliqués
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {parcours.acteurs?.map((acteur, i) => (
                          <span key={i} className="bg-[#571925]/10 text-[#571925] px-3 py-1 rounded-full text-sm">
                            {acteur}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Flux en sortie */}
                    <div>
                      <h3 className="font-semibold text-[#1a1a1a] mb-2">Flux et éléments en sortie</h3>
                      <ul className="list-disc list-inside space-y-1 text-gray-700">
                        {parcours.outputs?.map((output, i) => (
                          <li key={i}>{output}</li>
                        ))}
                      </ul>
                    </div>
                  </CardContent>
                </Card>

                {/* Objectifs Pédagogiques */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-xl text-[#cc5759] font-['Outfit'] flex items-center gap-2">
                      <Target className="h-5 w-5" />
                      Objectifs pédagogiques
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600 mb-4">À l'issue de ce parcours, l'utilisateur est capable de :</p>
                    <ul className="space-y-3">
                      {parcours.objectifs?.map((obj, i) => (
                        <li key={i} className="flex items-start gap-3">
                          <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 shrink-0" />
                          <span className="text-gray-700">{obj}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>

                {/* Bonnes Pratiques */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-xl text-[#cc5759] font-['Outfit'] flex items-center gap-2">
                      <ListChecks className="h-5 w-5" />
                      Bonnes pratiques
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3">
                      {parcours.bonnes_pratiques?.map((pratique, i) => (
                        <li key={i} className="flex items-start gap-3 p-3 bg-green-50 rounded-lg">
                          <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 shrink-0" />
                          <span className="text-gray-700">{pratique}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Scénarios Sections */}
            {parcours.etapes?.map((etape, index) => (
              activeSection === `scenario${index + 1}` && (
                <Card key={`scenario${index + 1}`} className="animate-fade-in" data-testid={`section-scenario${index + 1}`}>
                  <CardHeader>
                    <CardTitle className="text-xl text-[#cc5759] font-['Outfit']">
                      {etape.titre}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Scénario métier */}
                    <div className="bg-[#571925]/5 p-4 rounded-lg">
                      <h3 className="font-semibold text-[#571925] mb-2">Contexte du scénario</h3>
                      <p className="text-gray-700 italic">{etape.scenario}</p>
                    </div>

                    {/* Actions */}
                    <div>
                      <h3 className="font-semibold text-[#1a1a1a] mb-3">Actions clés</h3>
                      <ul className="space-y-2">
                        {etape.actions?.map((action, i) => (
                          <li key={i} className="flex items-center gap-2">
                            <div className="h-6 w-6 rounded-full bg-[#ff9a52] text-white text-xs flex items-center justify-center font-medium">
                              {i + 1}
                            </div>
                            <span className="text-gray-700">{action}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Video Section */}
                    <div>
                      <h3 className="font-semibold text-[#1a1a1a] mb-3 flex items-center gap-2">
                        <Video className="h-4 w-4 text-[#cc5759]" />
                        Vidéo de démonstration
                      </h3>
                      <div 
                        className={`video-placeholder aspect-video rounded-xl flex items-center justify-center cursor-pointer group relative overflow-hidden ${etape.video_url && getVideoThumbnail(etape.video_url) ? 'bg-transparent' : 'bg-gray-900'}`}
                        onClick={() => handleVideoPlay(index)}
                        data-testid={`video-placeholder-${index}`}
                      >
                        {etape.video_url && getVideoThumbnail(etape.video_url) ? (
                          <>
                            <img 
                              src={getVideoThumbnail(etape.video_url)}
                              alt={`Miniature - ${etape.titre}`}
                              className="absolute inset-0 w-full h-full object-cover rounded-xl"
                            />
                            <div className="absolute inset-0 bg-black/30 group-hover:bg-black/10 transition-colors rounded-xl"></div>
                            <div className="relative z-10 text-center">
                              <div className="h-16 w-16 rounded-full bg-white/90 flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform shadow-lg">
                                <Play className="h-8 w-8 text-[#571925] ml-1" />
                              </div>
                              <p className="text-white font-semibold text-sm drop-shadow-lg">
                                Cliquez pour voir la vidéo de démonstration
                              </p>
                              <p className="text-white/90 text-xs mt-1 drop-shadow">{etape.titre}</p>
                            </div>
                          </>
                        ) : (
                          <div className="text-center">
                            <div className="h-16 w-16 rounded-full bg-[#ff9a52] flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                              <Play className="h-8 w-8 text-white ml-1" />
                            </div>
                            <p className="text-white/70 text-sm">
                              Vidéo à venir
                            </p>
                            <p className="text-white/50 text-xs mt-1">{etape.titre}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )
            ))}

            {/* Quiz Section */}
            {activeSection === 'quiz' && (
              <Card className="animate-fade-in" data-testid="section-quiz">
                <CardHeader>
                  <CardTitle className="text-xl text-[#cc5759] font-['Outfit']">
                    Quiz — Évaluation des acquis
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-8">
                    <div className="h-20 w-20 rounded-full bg-[#ff9a52]/10 flex items-center justify-center mx-auto mb-4">
                      <HelpCircle className="h-10 w-10 text-[#ff9a52]" />
                    </div>
                    <h3 className="text-lg font-semibold text-[#1a1a1a] mb-2">
                      {quizResult?.passed ? 'Quiz validé !' : 'Prêt à tester vos connaissances ?'}
                    </h3>
                    <p className="text-gray-600 mb-6 max-w-md mx-auto">
                      {quizResult?.passed 
                        ? `Vous avez obtenu ${quizResult.score}% en ${quizResult.attempts} tentative(s).`
                        : '5 questions pour évaluer vos acquis. Seuil de validation : 75%'
                      }
                    </p>
                    <Button 
                      onClick={() => navigate(`/vipplus/parcours/${id}/quiz`)}
                      className="bg-[#ff9a52] hover:bg-[#e08540] text-white rounded-full px-8"
                      data-testid="start-quiz-btn"
                    >
                      {quizResult?.passed ? 'Refaire le quiz' : quizResult ? 'Réessayer le quiz' : 'Commencer le quiz'}
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Satisfaction Section */}
            {activeSection === 'satisfaction' && (
              <Card className="animate-fade-in" data-testid="section-satisfaction">
                <CardHeader>
                  <CardTitle className="text-xl text-[#cc5759] font-['Outfit'] flex items-center gap-2">
                    <ClipboardCheck className="h-5 w-5" />
                    Évaluation de la formation (Qualiopi)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-8">
                    <div className="h-20 w-20 rounded-full bg-[#571925]/10 flex items-center justify-center mx-auto mb-4">
                      <ClipboardCheck className="h-10 w-10 text-[#571925]" />
                    </div>
                    <h3 className="text-lg font-semibold text-[#1a1a1a] mb-2">
                      Questionnaire de satisfaction
                    </h3>
                    <p className="text-gray-600 mb-6 max-w-md mx-auto">
                      Votre avis nous est précieux. Conformément aux exigences Qualiopi, merci de remplir ce questionnaire pour nous aider à améliorer nos formations.
                    </p>
                    <Button 
                      onClick={() => navigate(`/vipplus/feedback/satisfaction?parcours=${id}`)}
                      className="bg-[#571925] hover:bg-[#3d1119] text-white rounded-full px-8"
                      data-testid="satisfaction-btn"
                    >
                      Donner mon avis
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-8">
              <Button 
                variant="outline" 
                onClick={() => {
                  const currentIndex = sections.findIndex(s => s.id === activeSection);
                  if (currentIndex > 0) {
                    handleSectionChange(sections[currentIndex - 1].id);
                  }
                }}
                disabled={activeSection === 'presentation'}
                className="border-[#571925] text-[#571925] hover:bg-[#571925] hover:text-white"
              >
                Section précédente
              </Button>
              <Button 
                onClick={() => {
                  const currentIndex = sections.findIndex(s => s.id === activeSection);
                  if (currentIndex < sections.length - 1) {
                    handleSectionChange(sections[currentIndex + 1].id);
                  }
                }}
                disabled={activeSection === 'satisfaction'}
                className="bg-[#ff9a52] hover:bg-[#e08540] text-white"
              >
                Section suivante
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </main>
        </div>
      </div>

      {/* Video Modal */}
      <Dialog open={videoModalOpen} onOpenChange={setVideoModalOpen}>
        <DialogContent className="max-w-4xl w-full">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-[#571925]">
              {currentVideo?.title || 'Vidéo de démonstration'}
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            {currentVideo?.url && (
              <div className="aspect-video bg-black rounded-lg overflow-hidden">
                <video 
                  controls 
                  autoPlay
                  className="w-full h-full"
                  src={currentVideo.url}
                >
                  <p className="text-white p-4">
                    Votre navigateur ne supporte pas la lecture vidéo.
                    <a href={currentVideo.url} className="text-blue-400 underline ml-2">
                      Télécharger la vidéo
                    </a>
                  </p>
                </video>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
